import React from 'react';
import { useAuth, api } from '../App';
import { LayoutDashboard, Users, FileText, LogOut, ChevronLeft, ChevronRight, UserCog, ArrowLeftCircle, Link2, MessageCircle, Archive, Menu, BarChart3, Database } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger, SheetClose } from './ui/sheet';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { toast } from 'sonner';

function TokenExpiryTicker() {
  const [daysLeft, setDaysLeft] = React.useState(null);
  React.useEffect(() => {
    api('/api/sync/status').then(d => {
      if (d?.fb_token_days_left !== null && d?.fb_token_days_left !== undefined) {
        setDaysLeft(d.fb_token_days_left);
      }
    }).catch(() => {});
  }, []);

  if (daysLeft === null) return null;

  const urgent = daysLeft <= 7;
  const warning = daysLeft <= 14;

  return (
    <div className={`mx-3 mb-2 px-3 py-2 rounded-lg text-xs ${
      urgent ? 'bg-red-500/20 text-red-200' : warning ? 'bg-amber-500/20 text-amber-200' : 'bg-white/5 text-white/50'
    }`}>
      <div className="font-semibold" data-testid="direct-meta-expiry-label">Direct Meta token</div>
      <div className={`text-lg font-bold ${urgent ? 'text-red-300' : warning ? 'text-amber-300' : 'text-white/70'}`} data-testid="direct-meta-expiry-reminder">
        {daysLeft < 0 ? 'Past saved expiry' : `${daysLeft}d to saved expiry`}
      </div>
      <div className="text-[10px] mt-0.5" data-testid="direct-meta-expiry-disclaimer">Date reminder · not Metricool</div>
    </div>
  );
}

const getNavItems = (role) => {
  const isInternal = role === 'super_admin' || role === 'internal';
  const items = [];
  if (!isInternal) {
    items.push({ key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard });
  }
  if (isInternal) {
    items.push({ key: 'clients', label: 'Clients', icon: Users });
    items.push({ key: 'metricool', label: 'Metricool', icon: BarChart3 });
    items.push({ key: 'sources', label: 'Data Connections', icon: Database });
  }
  items.push({ key: 'reports', label: 'Reports', icon: FileText });
  if (isInternal) {
    items.push({ key: 'engagement', label: 'Engagement', icon: MessageCircle });
    items.push({ key: 'sentiment', label: 'Sentiment', icon: MessageCircle });
  }
  if (role === 'super_admin') {
    items.push({ key: 'users', label: 'Users', icon: UserCog });
    items.push({ key: 'utm-defaults', label: 'UTM Tracking', icon: Link2 });
    items.push({ key: 'engagement-test', label: 'Reply Test', icon: MessageCircle });
    items.push({ key: 'archive', label: 'History Archive', icon: Archive });
  }
  return items;
};

export default function AppShell({ children, currentPage, navigate }) {
  const { user, handleLogout } = useAuth();
  const [collapsed, setCollapsed] = React.useState(false);
  const navItems = getNavItems(user?.role);
  const isImpersonating = user?.impersonating === true;

  const handleBackToAdmin = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Returned to admin view');
    window.location.reload();
  };

  const [barMinimized, setBarMinimized] = React.useState(false);

  return (
    <div className="flex min-h-screen bg-[hsl(var(--background))]">
      {/* Impersonation banner — collapsible */}
      {isImpersonating && !barMinimized && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white text-center py-1.5 text-xs font-medium flex items-center justify-center gap-3" data-testid="impersonation-banner">
          <span>Viewing as client: {user?.full_name}</span>
          <button onClick={handleBackToAdmin} className="bg-white/20 hover:bg-white/30 px-3 py-0.5 rounded-full text-xs font-semibold transition-colors" data-testid="back-to-admin-button">
            Back to Admin
          </button>
          <button onClick={() => setBarMinimized(true)} className="bg-white/20 hover:bg-white/30 w-5 h-5 rounded-full text-xs flex items-center justify-center" title="Minimize">
            ×
          </button>
        </div>
      )}
      {isImpersonating && barMinimized && (
        <button
          onClick={() => setBarMinimized(false)}
          className="fixed top-2 right-2 z-50 bg-amber-500 text-white w-8 h-8 rounded-full text-xs font-bold flex items-center justify-center shadow-lg hover:bg-amber-600 transition-colors"
          title="Show client bar"
        >
          !
        </button>
      )}
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full z-40 hidden md:flex flex-col ${
          collapsed ? 'w-[76px]' : 'w-[280px]'
        }`}
        style={{ background: '#1f2a4d' }}
        data-testid="app-sidebar"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5">
          {collapsed ? (
            <img src="/logos/freddy-symbol.png" alt="Freddy" className="w-10 h-10 object-contain" />
          ) : (
            <img src="/logos/freddy-script.png" alt="Freddy Media" className="h-10 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
          )}
        </div>

        {!collapsed && (
          <div className="px-5 pb-3">
            <span className="text-[9px] tracking-[0.16em] text-white/35 uppercase">
              Productizing Creativity
            </span>
          </div>
        )}

        <Separator className="bg-white/10" />

        {/* Navigation */}
        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1 sidebar-nav">
          <div className="px-3 pt-2 pb-2">
            {!collapsed && (
              <span className="text-[11px] font-semibold tracking-[0.14em] text-white/50 uppercase">
                Navigation
              </span>
            )}
          </div>
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = currentPage === item.key;
            return (
              <button
                key={item.key}
                onClick={() => navigate(item.key)}
                data-testid={`nav-${item.key}`}
                data-active={active}
                className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-white text-[#1f2a4d] shadow-sm'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon size={18} />
                {!collapsed && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Token expiry warning for super admin */}
        {!collapsed && user?.role === 'super_admin' && <TokenExpiryTicker />}

        {/* User section */}
        <div className="px-3 pb-4">
          <Separator className="bg-white/10 mb-3" />
          <div className={`flex items-center gap-3 px-3 py-2 ${collapsed ? 'justify-center' : ''}`}>
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-sm font-semibold">
              {user?.full_name?.[0] || user?.username?.[0] || 'U'}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">{user?.full_name || user?.username}</div>
                <div className="text-xs text-white/50 capitalize">{user?.role?.replace('_', ' ')}</div>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-button"
            className={`w-full mt-2 text-white/60 hover:text-white hover:bg-white/10 ${collapsed ? 'px-2' : ''}`}
          >
            <LogOut size={16} />
            {!collapsed && <span className="ml-2">Sign Out</span>}
          </Button>
        </div>

        {/* Collapse button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute top-6 -right-3 w-6 h-6 rounded-full bg-white border border-[hsl(var(--border))] shadow-sm flex items-center justify-center hover:bg-gray-50 transition-colors"
          data-testid="sidebar-collapse-button"
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </aside>

      {/* Main content */}
      <main
        className={`flex-1 min-w-0 w-full ${
          collapsed ? 'md:ml-[76px]' : 'md:ml-[280px]'
        }`}
      >
        <header className="md:hidden flex items-center justify-between gap-3 px-4 py-3 border-b bg-primary text-primary-foreground" data-testid="mobile-app-header">
          <img src="/logos/freddy-script.png" alt="Freddy Media" className="h-8 object-contain brightness-0 invert" />
          <Sheet><SheetTrigger asChild><Button variant="ghost" size="icon" className="min-h-11 min-w-11 hover:bg-white/10 hover:text-white" aria-label="Open navigation" data-testid="mobile-navigation-open"><Menu size={22} /></Button></SheetTrigger><SheetContent side="left" className="bg-primary text-primary-foreground flex flex-col w-4/5 max-w-sm" data-testid="mobile-navigation-panel"><SheetHeader><SheetTitle className="text-primary-foreground">Freddy Media</SheetTitle><SheetDescription className="text-primary-foreground/70">Reporting suite navigation</SheetDescription></SheetHeader><nav className="flex-1 min-h-0 overflow-y-auto space-y-1 py-4">{navItems.map(item => { const Icon = item.icon; return <SheetClose key={item.key} asChild><Button variant="ghost" onClick={() => navigate(item.key)} data-testid={`mobile-nav-${item.key}`} aria-current={currentPage === item.key ? 'page' : undefined} className={`w-full min-h-11 justify-start gap-3 hover:bg-white/10 hover:text-white ${currentPage === item.key ? 'bg-white/15 text-white font-semibold' : 'text-white/80'}`}><Icon size={18} />{item.label}</Button></SheetClose>; })}</nav><Button variant="ghost" className="min-h-11 justify-start text-white/80 hover:bg-white/10 hover:text-white" onClick={handleLogout} data-testid="mobile-logout-button"><LogOut size={16} className="mr-2" />Sign out</Button></SheetContent></Sheet>
        </header>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {children}
        </div>
      </main>
    </div>
  );
}
